/**
 * Firmen-Umschalter im Header. Zeigt die Firmen, zu denen der User gehört
 * (Admin zusätzlich „Alle Firmen"). Bei genau einer Firma und Nicht-Admin
 * wird nichts angezeigt.
 *
 * v1.3.0 (Chef-Ansicht): ein Admin sieht hier ALLE Firmen der Instanz, nicht
 * nur die, in denen er selbst Mitglied ist. Wählt er eine Firma, sieht er die
 * App so wie deren Chef — mit allen Chef-Funktionen (Löhne bleiben für Admins
 * 0,00, siehe backend/src/middleware/adminWageMask.ts).
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { companiesApi } from '../lib/companies';

export function CompanySwitcher() {
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const active = useAuth((s) => s.activeCompanyId);
  const setActive = useAuth((s) => s.setActiveCompany);
  const qc = useQueryClient();
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'superadmin');

  const { data: allCompanies } = useQuery({
    queryKey: ['companies', 'switcher'],
    queryFn: () => companiesApi.list(token!),
    enabled: isAdmin && !!token,
    staleTime: 60_000,
  });

  // Gespeicherte, inzwischen gelöschte Firma → zurück auf „Alle Firmen".
  useEffect(() => {
    if (isAdmin && active && allCompanies && !allCompanies.some((c) => c.id === active)) {
      setActive(null);
      void qc.resetQueries();
    }
  }, [isAdmin, active, allCompanies, setActive, qc]);

  if (!user) return null;

  const options: { id: string; name: string }[] =
    isAdmin && allCompanies
      ? allCompanies.map((c) => ({ id: c.id, name: c.name }))
      : user.memberships.map((m) => ({ id: m.companyId, name: m.companyName }));
  if (!isAdmin && options.length <= 1) return null;

  const value = active ?? 'all';
  return (
    <select
      value={value}
      onChange={(e) => {
        setActive(e.target.value === 'all' ? null : e.target.value);
        void qc.resetQueries();
      }}
      className="text-[11px] sm:text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 !min-h-0 max-w-[9rem] sm:max-w-[12rem] truncate"
      aria-label="Firma wählen"
      title={isAdmin ? 'Firma wählen — mit einer Firma siehst du die Chef-Ansicht' : 'Firma'}
    >
      {isAdmin && (
        <option value="all" style={{ backgroundColor: '#1f2a3d', color: '#e6ebf3' }}>
          Alle Firmen
        </option>
      )}
      {isAdmin && active && !options.some((o) => o.id === active) && (
        <option value={active} style={{ backgroundColor: '#1f2a3d', color: '#e6ebf3' }}>
          …
        </option>
      )}
      {options.map((m) => (
        <option key={m.id} value={m.id} style={{ backgroundColor: '#1f2a3d', color: '#e6ebf3' }}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

/**
 * v1.3.0: Hinweisleiste unter dem Header, solange ein Admin eine Firma aus
 * Chef-Sicht betrachtet — mit Rücksprung auf „Alle Firmen".
 */
export function AdminChefViewBanner({ onExit }: { onExit: () => void }) {
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const active = useAuth((s) => s.activeCompanyId);
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'superadmin');
  const { data: allCompanies } = useQuery({
    queryKey: ['companies', 'switcher'],
    queryFn: () => companiesApi.list(token!),
    enabled: isAdmin && !!token,
    staleTime: 60_000,
  });
  if (!isAdmin || !active) return null;
  const name = allCompanies?.find((c) => c.id === active)?.name ?? 'Firma';
  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-1.5 flex items-center justify-between gap-2 text-[11.5px] sm:text-xs">
        <span className="min-w-0 truncate">
          Chef-Ansicht: <strong className="font-semibold">{name}</strong> — alle Chef-Funktionen aktiv,
          Löhne verschlüsselt (0,00 €).
        </span>
        <button
          type="button"
          onClick={onExit}
          className="shrink-0 rounded bg-amber-500/20 hover:bg-amber-500/30 px-2 py-0.5 !min-h-0"
        >
          Beenden
        </button>
      </div>
    </div>
  );
}
